
import { VNode } from "vue";
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import vselect3 from "vselect3";

import { AlarmAction, AlarmActionType, AlarmActionTypeIdentifier } from "@/store/responses/types";
import ActionSetupActivateRelay from "@/components/setup/alarm-setup/actions/ActionSetupActivateRelay.vue";
import ActionSetupArmAlarm from "@/components/setup/alarm-setup/actions/ActionSetupArmAlarm.vue";
import ActionSetupArmSite from "@/components/setup/alarm-setup/actions/ActionSetupArmSite.vue";
import ActionSetupRecordCamera from "@/components/setup/alarm-setup/actions/ActionSetupRecordCamera.vue";
import ActionSetupSendEmail from "@/components/setup/alarm-setup/actions/ActionSetupSendEmail.vue";
import ActionSetupSendNotification from "@/components/setup/alarm-setup/actions/ActionSetupSendNotification.vue";

const Responses = namespace("responses");

const actionComponents: Record<string, any> = {
	[AlarmActionTypeIdentifier.RecordCamera]: ActionSetupRecordCamera,
	[AlarmActionTypeIdentifier.ActivateRelay]: ActionSetupActivateRelay,
	[AlarmActionTypeIdentifier.SendEmail]: ActionSetupSendEmail,
	[AlarmActionTypeIdentifier.ChangeSiteArmStatus]: ActionSetupArmSite,
	[AlarmActionTypeIdentifier.ChangeInputArmStatus]: ActionSetupArmAlarm,
	[AlarmActionTypeIdentifier.SendNotification]: ActionSetupSendNotification
};

@Component({
	components: {
		"action-setup-activate-relay": ActionSetupActivateRelay,
		"action-setup-arm-alarm": ActionSetupArmAlarm,
		"action-setup-arm-site": ActionSetupArmSite,
		"action-setup-record-camera": ActionSetupRecordCamera,
		"action-setup-send-email": ActionSetupSendEmail,
		"action-setup-send-notification": ActionSetupSendNotification,
		"v-select-3": vselect3
	}
})
export default class ActionSetupEntityModal extends Vue {
	@Responses.Action private createAlarmAction: (alarmAction: AlarmAction) => Promise<void>;
	@Responses.Action private deleteAlarmAction: (alarmAction: AlarmAction) => Promise<void>;
	@Responses.Action private updateAlarmAction: (alarmAction: AlarmAction) => Promise<void>;
	@Responses.Mutation private setCurrentAlarmAction: (alarmAction: AlarmAction) => void;
	@Responses.State("currentAlarmAction") private currentAlarmActionFromStore: AlarmAction;
	@Responses.State private supportedAlarmActionTypes: AlarmActionType[];

	private currentAlarmAction: AlarmAction;
	private deleteConfirmModalOpen: boolean = false;
	private formIsInvalid: boolean = true;
	private selectedAlarmActionTypeId: number = 0;

	private created(): void {
		this.currentAlarmAction = { ...this.currentAlarmActionFromStore };

		if (this.isEditing) {
			this.selectedAlarmActionTypeId = this.currentAlarmAction.responseActionTypeID;
		}
	}

	private handleModalHidden(): void {
		if (!this.deleteConfirmModalOpen) {
			this.setCurrentAlarmAction(null);
		}
	}

	private async handleSaveClick(): Promise<void> {
		//if this field is sent, it will be rejected due to XSS
		this.currentAlarmAction.description = "";

		if (this.isEditing) {
			await this.updateAlarmAction(this.currentAlarmAction);
			return;
		}

		await this.createAlarmAction(this.currentAlarmAction);
	}

	private handleDeleteClick(): void {
		this.deleteConfirmModalOpen = true;
	}

	private async handleDeleteConfirmClick(): Promise<void> {
		await this.deleteAlarmAction(this.currentAlarmAction);
		this.deleteConfirmModalOpen = false;
	}

	private handleDeleteConfirmModalHidden(): void {
		this.deleteConfirmModalOpen = false;
	}

	private get isEditing(): boolean {
		return this.currentAlarmAction.responseActionID > 0;
	}

	private handleValidationChanged(invalid: boolean): void {
		this.formIsInvalid = invalid;
	}

	private handleAlarmActionDetailsChanged(alarmAction: AlarmAction): void {
		this.currentAlarmAction = alarmAction;
	}

	private handleActionTypeSelected(): void {
		this.setCurrentAlarmAction({
			responseActionID: this.currentAlarmAction.responseActionID,
			responseID: this.currentAlarmAction.responseID,
			responseActionTypeID: this.selectedAlarmActionTypeId,
			objectID: null,
			settingsMeta: ""
		} as AlarmAction);

		this.formIsInvalid = true;
	}

	private get actionComponent(): VNode | undefined {
		return actionComponents[this.selectedAlarmActionTypeId];
	}
}
